import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import './blog.scss'
import { useQuery } from '@tanstack/react-query'
import { blogServices } from '../../services/blog-service'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { getFormattedDate } from '../../lib/utils/helpers'
import { Link, useNavigate } from 'react-router-dom'
import { usePageMeta } from '../../lib/seo/use-page-meta'
import { LinkedInIcon } from '../../lib/components/Icon'
import { BlogReadingTime } from '../../lib/components/BlogReadingTime/blog-reading-time'

type BlogCategory = 'All' | 'Learning' | 'Private Equity' | 'Market News'
interface BlogPost {
  id: number
  title: string
  sub_title: string
  slug: string
  content: string
  category: string
  banner_image: string
  created_at: string
  linkedin_url?: string
  reading_time_minutes?: number | null
}

export const CATEGORIES: { text: BlogCategory; value: string }[] = [
  { text: 'All', value: '' },
  { text: 'Learning', value: 'LEARNING' },
  { text: 'Private Equity', value: 'PRIVATE_EQUITY' },
  { text: 'Market News', value: 'MARKET_NEWS' },
]
const DEFAULT_LIMIT = 10
const DATE_FORMAT = 'MMMM DD, YYYY'

export const BlogPage = () => {
  const navigate = useNavigate()
  const { authToken } = useSelector((state: RootState) => state.investor)

  const [activeCategory, setActiveCategory] = useState<(typeof CATEGORIES)[number]>({
    text: 'All',
    value: '',
  })
  const [pagination, setPagination] = useState({
    total: 0,
    limit: DEFAULT_LIMIT,
    count: 0,
    offset: 0,
  })
  const [accumulatedBlogPosts, setAccumulatedBlogPosts] = useState<BlogPost[]>([])
  const hasLoggedVisitRef = useRef(false)

  const { data, isFetching } = useQuery({
    queryKey: ['blogPosts-v1', activeCategory.value, pagination.offset],
    queryFn: async () => {
      return blogServices.getBlogPosts(
        {
          limit: pagination.limit.toString(),
          offset: pagination.offset.toString(),
          category: activeCategory.value,
        },
        {
          Authorization: authToken ?? '',
        },
      )
    },
  })

  const handleLoadMore = useCallback(() => {
    setPagination((prev) => {
      const nextOffset = prev.offset + DEFAULT_LIMIT
      return { ...prev, offset: nextOffset }
    })
  }, [])

  useEffect(() => {
    if (hasLoggedVisitRef.current) return
    hasLoggedVisitRef.current = true
    logCrmEvent(EngagementEvents.VISITED_BLOG, { Authorization: authToken ?? '' })
  }, [authToken])

  useEffect(() => {
    if (!data) return
    if (data.pagination) setPagination(data.pagination)
    const pageOffset = data.pagination?.offset ?? 0
    const blogPosts = data.blogPosts ?? []
    if (pageOffset === 0) {
      setAccumulatedBlogPosts(blogPosts)
    } else {
      setAccumulatedBlogPosts((prev) => {
        const seenIds = new Set(prev.map((p: BlogPost) => p.id))
        const unique = blogPosts.filter((p: BlogPost) => !seenIds.has(p.id))
        return [...prev, ...unique]
      })
    }
  }, [data])

  const featuredPost = accumulatedBlogPosts[0]
  const gridPosts = accumulatedBlogPosts.slice(1)
  const hasMore = pagination.offset + pagination.limit < pagination.total
  const insightsJsonLd = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Insights',
        description: 'Stay ahead with insights, trends, and global markets from Credibila.',
        url: `${window.location.origin}/insights`,
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: accumulatedBlogPosts.map((post, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: post.title,
            url: `${window.location.origin}/insights/posts/${post.slug}`,
          })),
        },
      }),
    [accumulatedBlogPosts],
  )

  usePageMeta({
    title: 'Insights | Credibila',
    description: 'Stay ahead with insights, trends, and global markets from Credibila.',
    canonicalPath: '/insights',
    jsonLd: insightsJsonLd,
  })

  const handleCategoryChange = (cat: (typeof CATEGORIES)[number]) => {
    setActiveCategory(cat)
    setPagination((prev) => ({ ...prev, offset: 0 }))
    setAccumulatedBlogPosts([])
  }

  const handleBlogPostClick = (slug: string) => {
    navigate(`/insights/posts/${slug}`)
  }

  return (
    <div className='blog-page'>
      <section className='blog-hero-section' aria-label='Blog'>
        <div className='blog-hero-content'>
          <h1 className='blog-hero-title'>
            Stay ahead with Insights, Trends, Global Markets and more
          </h1>
        </div>
      </section>

      <section className='blog-body'>
        <nav className='blog-category-tabs' aria-label='Blog categories'>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.text}
              className={`blog-category-tab ${
                activeCategory.text === cat.text ? 'blog-category-tab--active' : ''
              }`}
              onClick={() => handleCategoryChange(cat)}
              type='button'
            >
              {cat.text}
            </button>
          ))}
        </nav>

        {isFetching && accumulatedBlogPosts.length === 0 && (
          <div className='blog-loading'>
            <div className='blog-loading__spinner'></div>
          </div>
        )}

        {accumulatedBlogPosts.length === 0 && !isFetching && (
          <p className='blog-empty'>No articles found in this category.</p>
        )}

        {featuredPost && (
          <article
            className='blog-featured-card'
            onClick={() => handleBlogPostClick(featuredPost.slug)}
          >
            <div className='blog-featured-card__image-wrapper'>
              <Link
                className='blog-featured-card__image-link'
                to={`/insights/posts/${featuredPost.slug}`}
              >
                <img
                  className='blog-featured-card__image'
                  src={featuredPost.banner_image}
                  alt={featuredPost.title}
                  loading='eager'
                />
              </Link>
            </div>
            <div className='blog-featured-card__body'>
              <span className='blog-card-category'>
                {CATEGORIES.find((c) => c.value === featuredPost.category)?.text}
              </span>
              <h2 className='blog-featured-card__title'>
                <Link className='blog-card-title-link' to={`/insights/posts/${featuredPost.slug}`}>
                  {featuredPost.title}
                </Link>
              </h2>
              <BlogReadingTime
                minutes={featuredPost.reading_time_minutes}
                rootClassName='blog-card-read-time blog-card-read-time--featured'
                iconClassName='blog-card-read-time__icon'
              />
              <p className='blog-featured-card__excerpt'>{featuredPost.sub_title}</p>
              <span className='blog-card-date'>
                {getFormattedDate(new Date(featuredPost.created_at), DATE_FORMAT)}
              </span>
              {featuredPost.linkedin_url && (
                <a
                  className='blog-featured-card__linkedin-link'
                  href={featuredPost.linkedin_url}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={(event) => event.stopPropagation()}
                >
                  <LinkedInIcon width='28' height='28' />
                </a>
              )}
            </div>
          </article>
        )}

        {gridPosts.length > 0 && (
          <div className='blog-grid'>
            {gridPosts.map((post) => (
              <article
                key={post.id}
                className='blog-card'
                onClick={() => handleBlogPostClick(post.slug)}
              >
                <div className='blog-card__image-wrapper'>
                  <Link className='blog-card__image-link' to={`/insights/posts/${post.slug}`}>
                    <img
                      className='blog-card__image'
                      src={post.banner_image}
                      alt={post.title}
                      loading='lazy'
                    />
                  </Link>
                </div>
                <div className='blog-card__body'>
                  <span className='blog-card-category'>
                    {CATEGORIES.find((c) => c.value === post.category)?.text}
                  </span>
                  <h3 className='blog-card__title'>
                    <Link className='blog-card-title-link' to={`/insights/posts/${post.slug}`}>
                      {post.title}
                    </Link>
                  </h3>
                  <BlogReadingTime
                    minutes={post.reading_time_minutes}
                    rootClassName='blog-card-read-time'
                    iconClassName='blog-card-read-time__icon'
                  />
                  <p className='blog-card__excerpt'>{post.sub_title}</p>
                  <span className='blog-card-date'>
                    {getFormattedDate(new Date(post.created_at), DATE_FORMAT)}
                  </span>
                  {post.linkedin_url && (
                    <a
                      className='blog-card__linkedin-link'
                      href={post.linkedin_url}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={(event) => event.stopPropagation()}
                    >
                      <LinkedInIcon width='24' height='24' />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {hasMore && (
          <div className='blog-load-more-wrapper'>
            <button
              className='blog-load-more-btn'
              disabled={isFetching}
              onClick={handleLoadMore}
              type='button'
            >
              {isFetching ? 'Loading...' : 'Load more articles'}
            </button>
          </div>
        )}
      </section>
    </div>
  )
}
